// ---------------------------------------------------------------------------
// #region DC_FRAMING_MIRROR
// The code in this region is mirrored verbatim into the desktop bridge's injected
// script (apps/electron/src/main/public-access-bridge.ts) and checked by a sync
// test (public-access-bridge-framing-sync.test.ts). Keep it plain JS: no TypeScript
// type annotations, no imports. When editing, edit here then re-copy into the bridge
// template string — the sync test fails until the two copies match.
// ---------------------------------------------------------------------------
export function __dcfNextMsgId(prev) {
  return (prev + 1) >>> 0
}

export function __dcfResolveChunkBudgetBytes(maxMessageSize) {
  var DEFAULT_BUDGET = 16 * 1024
  var MAX_BUDGET = 64 * 1024
  var OVERHEAD = 64
  if (typeof maxMessageSize === 'number' && isFinite(maxMessageSize) && maxMessageSize > 0) {
    var usable = maxMessageSize - OVERHEAD
    if (usable < 1024) usable = 1024
    return Math.min(usable, MAX_BUDGET)
  }
  return DEFAULT_BUDGET
}

export function __dcfBytesToBase64(bytes) {
  var binary = ''
  var chunkSize = 0x8000
  for (var i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

export function __dcfBase64ToBytes(base64) {
  var binary = atob(base64)
  var bytes = new Uint8Array(binary.length)
  for (var i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

// Split `message` into ordered frames. Each frame is "1|msgId|index|count\n<base64>".
// Chunking is on UTF-8 byte boundaries (snapped off multi-byte sequences) so multibyte
// characters and surrogate pairs are never split; base64 keeps payload size predictable.
export function __dcfEncodeFrames(message, msgId, budgetBytes) {
  var bytes = new TextEncoder().encode(message)
  if (bytes.length === 0) {
    return ['1|' + msgId + '|0|1\n']
  }
  var rawBudget = Math.floor(budgetBytes / 4) * 3
  if (rawBudget < 3) rawBudget = 3
  var slices = []
  var i = 0
  while (i < bytes.length) {
    var end = i + rawBudget
    if (end >= bytes.length) {
      end = bytes.length
    } else {
      // Snap end down so we never cut inside a UTF-8 multi-byte sequence.
      while (end > i && (bytes[end] & 0xC0) === 0x80) {
        end -= 1
      }
      if (end === i) end = Math.min(i + rawBudget, bytes.length)
    }
    slices.push(bytes.subarray(i, end))
    i = end
  }
  var count = slices.length
  var frames = []
  for (var index = 0; index < count; index++) {
    frames.push('1|' + msgId + '|' + index + '|' + count + '\n' + __dcfBytesToBase64(slices[index]))
  }
  return frames
}

// Stateful reassembler factory. On any malformed/out-of-sequence/oversized input it
// calls onError and resets, so a corrupt stream can never wedge it.
export function __dcfCreateReassembler(opts) {
  var maxBytes = opts && opts.maxBytes ? opts.maxBytes : 32 * 1024 * 1024
  var maxChunks = opts && opts.maxChunks ? opts.maxChunks : 100000
  var onError = opts && opts.onError ? opts.onError : function () {}

  var curId = -1
  var started = false
  var expectedCount = 0
  var nextIndex = 0
  var buffers = []
  var totalBytes = 0

  function reset() {
    started = false
    curId = -1
    expectedCount = 0
    nextIndex = 0
    buffers = []
    totalBytes = 0
  }

  function fail(reason) {
    reset()
    onError(new Error('datachannel-framing: ' + reason))
    return null
  }

  function push(frame) {
    var nl = frame.indexOf('\n')
    if (nl < 0) return fail('missing header delimiter')
    var header = frame.slice(0, nl)
    var payloadB64 = frame.slice(nl + 1)
    var parts = header.split('|')
    if (parts.length !== 4 || parts[0] !== '1') return fail('bad header/version')
    var msgId = parseInt(parts[1], 10)
    var index = parseInt(parts[2], 10)
    var count = parseInt(parts[3], 10)
    if (!isFinite(msgId) || !isFinite(index) || !isFinite(count)) return fail('non-numeric header')
    if (count < 1 || index < 0 || index >= count) return fail('bad index/count')
    if (count > maxChunks) return fail('count exceeds limit')

    if (index === 0) {
      started = true
      curId = msgId
      expectedCount = count
      nextIndex = 0
      buffers = []
      totalBytes = 0
    } else if (!started || msgId !== curId || index !== nextIndex || count !== expectedCount) {
      return fail('out-of-sequence chunk')
    }

    var bytes
    try {
      bytes = __dcfBase64ToBytes(payloadB64)
    } catch (e) {
      return fail('base64 decode failed')
    }
    totalBytes += bytes.length
    if (totalBytes > maxBytes) return fail('reassembly buffer overflow')
    buffers.push(bytes)
    nextIndex = index + 1

    if (nextIndex === expectedCount) {
      var total = new Uint8Array(totalBytes)
      var offset = 0
      for (var k = 0; k < buffers.length; k++) {
        total.set(buffers[k], offset)
        offset += buffers[k].length
      }
      reset()
      return new TextDecoder().decode(total)
    }
    return null
  }

  return { push: push }
}
// ---------------------------------------------------------------------------
// #endregion DC_FRAMING_MIRROR
// ---------------------------------------------------------------------------
